import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, ClipboardList, Factory, Hammer, Layers, Plus, Search, Trash2, X } from "lucide-react";
import { api, money } from "@/lib/api";
import { useEscape } from "@/lib/useEscape";
import { Pagination, usePagination } from "@/components/Pagination";
import { SearchSelect } from "@/components/SearchSelect";

const ORDER_STATUS = { DRAFT: "Draft", IN_PROGRESS: "Sedang Diproses", DONE: "Selesai" };
const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

function Msg({ message, onClose, testid }) {
  if (!message) return null;
  return <div data-testid={testid} className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button onClick={onClose}><X size={14} /></button></div>;
}

/* ================= BOM ================= */
export function Boms() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState(null);
  const load = () => api("/boms").then(setRows).catch((e) => setMessage({ text: e.message, type: "error" }));
  useEffect(() => { load(); api("/products").then(setProducts).catch(() => {}); }, []);
  const filtered = rows.filter((r) => [r.name, r.product_name].join(" ").toLowerCase().includes(search.toLowerCase()));
  const pager = usePagination(filtered, 25, search);
  const remove = async () => {
    try {
      await api(`/boms/${deleting.id}`, { method: "DELETE" });
      setRows((prev) => prev.filter((x) => x.id !== deleting.id));
      setMessage({ text: `BOM "${deleting.name}" berhasil dihapus`, type: "success" });
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
    setDeleting(null);
  };
  return (
    <div className="workflow-page">
      <div className="page-heading">
        <div><p className="eyebrow">PRODUKSI</p><h1>Bill of Materials</h1><p className="muted">Resep produksi: bahan baku, tenaga kerja, dan overhead per unit produk jadi.</p></div>
        <button data-testid="bom-create-button" className="primary-btn" onClick={() => setEditing({})}><Plus size={16} /> Buat BOM</button>
      </div>
      <Msg message={message} onClose={() => setMessage(null)} testid={message?.type === "error" ? "bom-error-message" : "bom-success-message"} />
      <div className="workflow-toolbar surface">
        <div className="workflow-search"><Search size={16} /><input data-testid="bom-search-input" placeholder="Cari BOM..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
        <span className="record-count">{filtered.length} data</span>
      </div>
      <div className="surface workflow-table-wrap">
        <table className="workflow-table" data-testid="bom-table">
          <thead><tr><th>NAMA BOM</th><th>PRODUK JADI</th><th className="right">JML BAHAN</th><th className="right">BIAYA BAHAN/UNIT</th><th className="right">TENAGA KERJA/UNIT</th><th className="right">OVERHEAD/UNIT</th><th className="right">TOTAL/UNIT</th><th>AKSI</th></tr></thead>
          <tbody>
            {pager.pageItems.map((r) => (
              <tr key={r.id} onClick={() => setEditing(r)} data-testid={`bom-row-${r.id}`}>
                <td className="primary-cell">{r.name}</td>
                <td>{r.product_name}</td>
                <td className="right mono">{(r.lines || []).length}</td>
                <td className="right mono">{money(r.material_cost)}</td>
                <td className="right mono">{money(r.labor_cost)}</td>
                <td className="right mono">{money(r.overhead_cost)}</td>
                <td className="right mono">{money(r.unit_cost)}</td>
                <td><span className="row-actions">
                  <button data-testid={`bom-edit-${r.id}`} className="table-action" onClick={(e) => { e.stopPropagation(); setEditing(r); }}>Edit</button>
                  <button data-testid={`bom-delete-${r.id}`} className="table-action danger" onClick={(e) => { e.stopPropagation(); setDeleting(r); }}>Hapus</button>
                </span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="empty-state" data-testid="bom-empty-state"><Layers size={25} /><strong>Belum ada BOM</strong><span>Buat resep produksi pertama Anda.</span></div>}
        <Pagination pager={pager} testid="bom-pagination" />
      </div>
      {editing && <BomForm initial={editing.id ? editing : null} products={products}
        onClose={() => setEditing(null)}
        onSaved={(r, isEdit) => { setEditing(null); load(); setMessage({ text: `BOM "${r.name}" berhasil ${isEdit ? "diperbarui" : "disimpan"}`, type: "success" }); }} />}
      {deleting && (
        <div className="modal-overlay" data-testid="bom-delete-modal" onClick={() => setDeleting(null)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="form-panel">
              <div className="section-head"><div><p className="eyebrow">KONFIRMASI HAPUS</p><h2>Hapus BOM?</h2></div><button className="icon-btn" onClick={() => setDeleting(null)}><X size={18} /></button></div>
              <p className="muted delete-warning">"{deleting.name}" akan dihapus permanen. BOM yang sudah dipakai perintah produksi tidak dapat dihapus.</p>
              <div className="form-actions">
                <button data-testid="bom-delete-confirm" className="danger-btn" onClick={remove}>Ya, Hapus</button>
                <button data-testid="bom-delete-cancel" className="secondary-btn" onClick={() => setDeleting(null)}>Batal</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BomForm({ initial, products, onClose, onSaved }) {
  const goods = products.filter((p) => p.type !== "Service");
  const [productId, setProductId] = useState(initial?.product_id || "");
  const [name, setName] = useState(initial?.name || "");
  const [labor, setLabor] = useState(initial?.labor_cost || 0);
  const [overhead, setOverhead] = useState(initial?.overhead_cost || 0);
  const [notes, setNotes] = useState(initial?.notes || "");
  const [lines, setLines] = useState(initial?.lines?.map((l) => ({ product_id: l.product_id, quantity: l.quantity })) || [{ product_id: "", quantity: 1 }]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pmap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const setLine = (i, k, v) => setLines(lines.map((l, x) => (x === i ? { ...l, [k]: v } : l)));
  const material = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (pmap[l.product_id]?.purchase_price || 0), 0);
  const total = material + (Number(labor) || 0) + (Number(overhead) || 0);
  const submit = async () => {
    setBusy(true); setError("");
    try {
      const payload = { product_id: productId, name, labor_cost: Number(labor) || 0, overhead_cost: Number(overhead) || 0, notes, lines: lines.filter((l) => l.product_id).map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity) || 0 })) };
      const r = initial ? await api(`/boms/${initial.id}`, { method: "PATCH", body: JSON.stringify(payload) }) : await api("/boms", { method: "POST", body: JSON.stringify(payload) });
      onSaved(r, Boolean(initial));
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const valid = productId && lines.some((l) => l.product_id && Number(l.quantity) > 0);
  return (
    <div className="modal-overlay" data-testid="bom-form-modal">
      <div className="modal-panel wide" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel" data-testid="bom-form">
          <div className="section-head">
            <div><p className="eyebrow">PRODUKSI</p><h2>{initial ? "Edit" : "Buat"} Bill of Materials</h2></div>
            <button data-testid="bom-form-close" className="icon-btn" onClick={onClose}><X size={18} /></button>
          </div>
          {error && <div className="journal-balance bad" data-testid="bom-form-error">{error}</div>}
          <div className="form-grid">
            <label>Produk jadi *
              <SearchSelect testid="bom-field-product" value={productId} disabled={Boolean(initial)} placeholder="Pilih produk..."
                onChange={(v) => { setProductId(v); if (!name) setName(pmap[v] ? `BOM ${pmap[v].name}` : ""); }}
                options={goods.map((p) => ({ value: p.id, label: p.name }))} />
            </label>
            <label>Nama BOM<input data-testid="bom-field-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Otomatis dari nama produk" /></label>
          </div>
          <table className="journal-line-table" data-testid="bom-line-table">
            <thead><tr><th>BAHAN BAKU</th><th className="right">QTY / UNIT</th><th className="right">HARGA BELI</th><th className="right">JUMLAH</th><th /></tr></thead>
            <tbody>
              {lines.map((line, i) => (
                <tr key={i}>
                  <td>
                    <SearchSelect testid={`bom-line-product-${i}`} value={line.product_id} placeholder="Pilih bahan..."
                      onChange={(v) => setLine(i, "product_id", v)}
                      options={goods.filter((p) => p.id !== productId).map((p) => ({ value: p.id, label: p.name, sub: `stok ${p.stock ?? 0}` }))} />
                  </td>
                  <td><input data-testid={`bom-line-qty-${i}`} className="right" type="number" min="0" step="any" value={line.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} /></td>
                  <td className="right mono">{money(pmap[line.product_id]?.purchase_price || 0)}</td>
                  <td className="right mono">{money((Number(line.quantity) || 0) * (pmap[line.product_id]?.purchase_price || 0))}</td>
                  <td>{lines.length > 1 && <button data-testid={`bom-line-remove-${i}`} className="icon-btn" onClick={() => setLines(lines.filter((_, x) => x !== i))}><Trash2 size={15} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button data-testid="bom-add-line-button" className="text-btn" onClick={() => setLines([...lines, { product_id: "", quantity: 1 }])}><Plus size={14} /> Tambah bahan</button>
          <div className="form-grid" style={{ marginTop: 14 }}>
            <label>Biaya tenaga kerja / unit<input data-testid="bom-field-labor" type="number" min="0" value={labor} onChange={(e) => setLabor(e.target.value)} /></label>
            <label>Biaya overhead / unit<input data-testid="bom-field-overhead" type="number" min="0" value={overhead} onChange={(e) => setOverhead(e.target.value)} /></label>
          </div>
          <label className="bom-notes">Catatan<input data-testid="bom-field-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Opsional" /></label>
          <div className="doc-totals v2 bom-totals" data-testid="bom-cost-preview">
            <div><span>Biaya bahan / unit</span><b>{money(material)}</b></div>
            <div><span>Tenaga kerja + overhead / unit</span><b>{money((Number(labor) || 0) + (Number(overhead) || 0))}</b></div>
            <div className="grand"><span>Estimasi biaya / unit</span><b>{money(total)}</b></div>
          </div>
          <div className="form-actions">
            <button data-testid="bom-save-button" className="primary-btn" disabled={!valid || busy} onClick={submit}>{busy ? "Menyimpan..." : initial ? "Simpan Perubahan" : "Simpan BOM"}</button>
            <button data-testid="bom-cancel-button" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= Perintah Produksi ================= */
export default function ProductionOrders() {
  const [rows, setRows] = useState([]);
  const [boms, setBoms] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState(null);
  const load = () => Promise.all([api("/production-orders"), api("/production-orders/summary")]).then(([r, s]) => { setRows(r); setSummary(s); }).catch((e) => setMessage({ text: e.message, type: "error" }));
  useEffect(() => { load(); api("/boms").then(setBoms).catch(() => {}); api("/warehouses").then(setWarehouses).catch(() => {}); }, []);
  const filtered = rows.filter((r) => [r.number, r.product_name, r.warehouse_name, r.status].join(" ").toLowerCase().includes(search.toLowerCase()));
  const pager = usePagination(filtered, 25, search);
  const doAction = async (order, action, okText) => {
    try {
      const r = await api(`/production-orders/${order.id}/${action}`, { method: "POST" });
      setRows((prev) => prev.map((x) => (x.id === r.id ? r : x)));
      setSelected(r);
      api("/production-orders/summary").then(setSummary).catch(() => {});
      setMessage({ text: okText.replace("{n}", r.number), type: "success" });
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
  };
  const remove = async () => {
    try {
      await api(`/production-orders/${deleting.id}`, { method: "DELETE" });
      setRows((prev) => prev.filter((x) => x.id !== deleting.id));
      setSelected(null);
      setMessage({ text: `${deleting.number} berhasil dihapus`, type: "success" });
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
    setDeleting(null);
  };
  return (
    <div className="workflow-page">
      <div className="page-heading">
        <div><p className="eyebrow">PRODUKSI</p><h1>Perintah Produksi</h1><p className="muted">Mulai produksi memotong stok bahan (jurnal WIP), selesai menambah stok barang jadi dengan HPP akurat.</p></div>
        <button data-testid="po-create-button" className="primary-btn" onClick={() => setEditing({})}><Plus size={16} /> Buat Perintah Produksi</button>
      </div>
      <Msg message={message} onClose={() => setMessage(null)} testid={message?.type === "error" ? "po-error-message" : "po-success-message"} />
      {summary && (
        <div className="prod-cards" data-testid="po-summary-cards">
          <div className="prod-card blue"><div className="prod-card-head"><span>Draft</span><ClipboardList size={15} /></div><small>Perintah produksi</small><strong>{summary.draft}</strong></div>
          <div className="prod-card yellow"><div className="prod-card-head"><span>Sedang Diproses</span><Hammer size={15} /></div><small>Perintah produksi</small><strong>{summary.in_progress}</strong></div>
          <div className="prod-card green"><div className="prod-card-head"><span>Selesai Bulan Ini</span><CheckCircle2 size={15} /></div><small>Perintah produksi</small><strong>{summary.done_this_month}</strong></div>
          <div className="prod-card teal"><div className="prod-card-head"><span>Biaya Produksi Bulan Ini</span><Factory size={15} /></div><small>Total HPP selesai</small><strong className="po-cost">{money(summary.cost_this_month)}</strong></div>
        </div>
      )}
      <div className="workflow-toolbar surface">
        <div className="workflow-search"><Search size={16} /><input data-testid="po-search-input" placeholder="Cari perintah produksi..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
        <span className="record-count">{filtered.length} data</span>
      </div>
      <div className="surface workflow-table-wrap">
        <table className="workflow-table" data-testid="po-table">
          <thead><tr><th>NOMOR</th><th>TANGGAL</th><th>PRODUK JADI</th><th className="right">QTY</th><th>GUDANG</th><th className="right">TOTAL BIAYA</th><th>STATUS</th><th>AKSI</th></tr></thead>
          <tbody>
            {pager.pageItems.map((r) => (
              <tr key={r.id} onClick={() => setSelected(r)} data-testid={`po-row-${r.id}`}>
                <td className="primary-cell">{r.number}</td>
                <td>{fmtDate(r.date)}</td>
                <td>{r.product_name}</td>
                <td className="right mono">{r.quantity}</td>
                <td>{r.warehouse_name}</td>
                <td className="right mono">{money(r.total_cost)}</td>
                <td><span className={`status ${r.status.toLowerCase()}`}>{ORDER_STATUS[r.status] || r.status}</span></td>
                <td><span className="row-actions">
                  {r.status === "DRAFT" && <button data-testid={`po-edit-${r.id}`} className="table-action" onClick={(e) => { e.stopPropagation(); setEditing(r); }}>Edit</button>}
                  {r.status === "DRAFT" && <button data-testid={`po-delete-${r.id}`} className="table-action danger" onClick={(e) => { e.stopPropagation(); setDeleting(r); }}>Hapus</button>}
                </span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="empty-state" data-testid="po-empty-state"><Factory size={25} /><strong>Belum ada perintah produksi</strong><span>Buat BOM terlebih dahulu, lalu buat perintah produksi.</span></div>}
        <Pagination pager={pager} testid="po-pagination" />
      </div>
      {editing && <OrderForm initial={editing.id ? editing : null} boms={boms} warehouses={warehouses}
        onClose={() => setEditing(null)}
        onSaved={(r, isEdit) => { setEditing(null); load(); setMessage({ text: `${r.number} berhasil ${isEdit ? "diperbarui" : "dibuat"} — mulai produksi lewat detail`, type: "success" }); }} />}
      {selected && <OrderDrawer order={selected} onClose={() => setSelected(null)}
        onStart={() => doAction(selected, "start", "Produksi {n} dimulai — stok bahan dipotong & jurnal WIP diposting")}
        onFinish={() => doAction(selected, "finish", "Produksi {n} selesai — stok barang jadi bertambah & HPP diposting")}
        onCancel={() => doAction(selected, "cancel", "Produksi {n} dibatalkan — bahan dikembalikan & jurnal di-void")}
        onEdit={() => { setEditing(selected); setSelected(null); }}
        onDelete={() => setDeleting(selected)} />}
      {deleting && (
        <div className="modal-overlay" data-testid="po-delete-modal" onClick={() => setDeleting(null)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="form-panel">
              <div className="section-head"><div><p className="eyebrow">KONFIRMASI HAPUS</p><h2>Hapus Perintah Produksi?</h2></div><button className="icon-btn" onClick={() => setDeleting(null)}><X size={18} /></button></div>
              <p className="muted delete-warning">"{deleting.number}" akan dihapus permanen. Hanya perintah berstatus Draft yang dapat dihapus.</p>
              <div className="form-actions">
                <button data-testid="po-delete-confirm" className="danger-btn" onClick={remove}>Ya, Hapus</button>
                <button data-testid="po-delete-cancel" className="secondary-btn" onClick={() => setDeleting(null)}>Batal</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OrderForm({ initial, boms, warehouses, onClose, onSaved }) {
  const [bomId, setBomId] = useState(initial?.bom_id || "");
  const [qty, setQty] = useState(initial?.quantity || 1);
  const [warehouseId, setWarehouseId] = useState(initial?.warehouse_id || warehouses[0]?.id || "");
  const [date, setDate] = useState(initial?.date || new Date().toISOString().slice(0, 10));
  const [memo, setMemo] = useState(initial?.memo || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [whStock, setWhStock] = useState(null);
  useEffect(() => {
    if (!warehouseId) { setWhStock(null); return; }
    let active = true;
    api(`/inventory/warehouse-stock/${warehouseId}`).then((r) => { if (active) setWhStock(r.stock || {}); }).catch(() => { if (active) setWhStock(null); });
    return () => { active = false; };
  }, [warehouseId]);
  const bom = boms.find((b) => b.id === bomId);
  const q = Number(qty) || 0;
  const material = (bom?.material_cost || 0) * q;
  const conv = ((bom?.labor_cost || 0) + (bom?.overhead_cost || 0)) * q;
  const submit = async () => {
    setBusy(true); setError("");
    try {
      const payload = { bom_id: bomId, quantity: q, warehouse_id: warehouseId, date, memo };
      const r = initial ? await api(`/production-orders/${initial.id}`, { method: "PATCH", body: JSON.stringify(payload) }) : await api("/production-orders", { method: "POST", body: JSON.stringify(payload) });
      onSaved(r, Boolean(initial));
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <div className="modal-overlay" data-testid="po-form-modal">
      <div className="modal-panel wide" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel" data-testid="po-form">
          <div className="section-head">
            <div><p className="eyebrow">PRODUKSI</p><h2>{initial ? "Edit" : "Buat"} Perintah Produksi</h2></div>
            <button data-testid="po-form-close" className="icon-btn" onClick={onClose}><X size={18} /></button>
          </div>
          {error && <div className="journal-balance bad" data-testid="po-form-error">{error}</div>}
          <div className="form-grid">
            <label>Bill of Materials *
              <SearchSelect testid="po-field-bom" value={bomId} onChange={(v) => setBomId(v)} placeholder="Pilih BOM..."
                options={boms.map((b) => ({ value: b.id, label: `${b.name} — ${b.product_name}` }))} />
            </label>
            <label>Jumlah produksi *<input data-testid="po-field-qty" type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} /></label>
            <label>Gudang *
              <SearchSelect testid="po-field-warehouse" value={warehouseId} onChange={(v) => setWarehouseId(v)} placeholder="Pilih gudang..."
                options={warehouses.map((w) => ({ value: w.id, label: w.name }))} />
            </label>
            <label>Tanggal *<input data-testid="po-field-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
          </div>
          <label className="bom-notes">Memo<input data-testid="po-field-memo" value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Opsional" /></label>
          {bom && q > 0 && (
            <>
              <div className="drawer-section-title">Kebutuhan Bahan Baku</div>
              <table className="doc-line-table v2" data-testid="po-requirements">
                <thead><tr><th>BAHAN</th><th className="right">DIBUTUHKAN</th><th className="right">STOK GUDANG</th><th className="right">EST. BIAYA</th></tr></thead>
                <tbody>
                  {(bom.lines || []).map((l, i) => {
                    const needed = l.quantity * q;
                    const avail = whStock ? (whStock[l.product_id] ?? 0) : (l.stock ?? 0);
                    const short = avail < needed;
                    return <tr key={i}><td>{l.product_name}</td><td className="right mono">{needed}</td><td className={`right mono ${short ? "po-short" : ""}`}>{avail}{short ? " ⚠" : ""}</td><td className="right mono">{money(l.unit_cost * needed)}</td></tr>;
                  })}
                </tbody>
              </table>
              <div className="doc-totals v2 bom-totals" data-testid="po-cost-preview">
                <div><span>Biaya bahan</span><b>{money(material)}</b></div>
                <div><span>Tenaga kerja + overhead</span><b>{money(conv)}</b></div>
                <div className="grand"><span>Estimasi total biaya</span><b>{money(material + conv)}</b></div>
              </div>
            </>
          )}
          <div className="form-actions">
            <button data-testid="po-save-button" className="primary-btn" disabled={!bomId || q <= 0 || !warehouseId || busy} onClick={submit}>{busy ? "Menyimpan..." : initial ? "Simpan Perubahan" : "Simpan sebagai Draft"}</button>
            <button data-testid="po-cancel-button" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrderDrawer({ order, onClose, onStart, onFinish, onCancel, onEdit, onDelete }) {
  useEscape(onClose);
  const conv = (order.labor_cost || 0) + (order.overhead_cost || 0);
  return (
    <>
    <div className="drawer-backdrop" data-testid="po-drawer-backdrop" onClick={onClose} />
    <div className="detail-drawer v2" data-testid="po-detail-drawer">
      <div className="drawer-hero">
        <div className="drawer-hero-top">
          <div>
            <p className="eyebrow">PERINTAH PRODUKSI</p>
            <h2>{order.number}</h2>
            <p className="drawer-hero-party">{order.product_name} × {order.quantity} · {order.warehouse_name}</p>
          </div>
          <button data-testid="po-close-detail" className="icon-btn light" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="drawer-hero-meta">
          <span className={`status ${order.status.toLowerCase()}`}>{ORDER_STATUS[order.status] || order.status}</span>
          <span>{fmtDate(order.date)}</span>
        </div>
        <div className="drawer-hero-stats">
          <div><small>Biaya Bahan</small><b>{money(order.material_cost)}</b></div>
          <div><small>TK + Overhead</small><b>{money(conv)}</b></div>
          <div className="ok"><small>Total Biaya</small><b>{money(order.total_cost)}</b></div>
        </div>
      </div>
      <div className="drawer-body">
        {order.status === "DONE" && (
          <p className="muted ju-auto-note" data-testid="po-done-note">Produksi selesai — {order.quantity} unit <b>{order.product_name}</b> masuk stok dengan HPP <b>{money(order.unit_cost || 0)}</b>/unit. Jurnal produksi diposting otomatis.</p>
        )}
        {order.status === "IN_PROGRESS" && (
          <p className="muted ju-auto-note" data-testid="po-wip-note">Bahan baku sudah dipotong dari gudang dan tercatat di akun <b>Barang Dalam Proses (1-10202)</b>.</p>
        )}
        <div className="drawer-section-title">Informasi Produksi</div>
        <div className="detail-fields v2">
          <div><small>Nomor</small><strong>{order.number}</strong></div>
          <div><small>BOM</small><strong>{order.bom_name}</strong></div>
          <div><small>Produk Jadi</small><strong>{order.product_name}</strong></div>
          <div><small>Jumlah</small><strong>{order.quantity}</strong></div>
          <div><small>Gudang</small><strong>{order.warehouse_name}</strong></div>
          <div><small>Tanggal</small><strong>{fmtDate(order.date)}</strong></div>
          {order.unit_cost != null && order.status === "DONE" && <div><small>HPP / Unit</small><strong>{money(order.unit_cost)}</strong></div>}
          {order.memo && <div><small>Memo</small><strong>{order.memo}</strong></div>}
        </div>
        <div className="doc-lines v2">
          <div className="drawer-section-title">Bahan Baku</div>
          <table className="doc-line-table v2">
            <thead><tr><th>BAHAN</th><th className="right">QTY/UNIT</th><th className="right">DIBUTUHKAN</th><th className="right">HARGA</th><th className="right">JUMLAH</th></tr></thead>
            <tbody>
              {(order.lines || []).map((l, i) => (
                <tr key={i}><td>{l.product_name}</td><td className="right mono">{l.qty_per_unit}</td><td className="right mono">{l.needed}</td><td className="right mono">{money(l.unit_cost)}</td><td className="right mono">{money(l.amount)}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="doc-totals v2">
            <div><span>Biaya bahan</span><b>{money(order.material_cost)}</b></div>
            <div><span>Tenaga kerja</span><b>{money(order.labor_cost)}</b></div>
            <div><span>Overhead</span><b>{money(order.overhead_cost)}</b></div>
            <div className="grand"><span>Total biaya produksi</span><b>{money(order.total_cost)}</b></div>
          </div>
        </div>
        {order.status !== "DONE" && (
          <>
            <div className="drawer-section-title">Tindakan</div>
            <div className="drawer-actions v2">
              {order.status === "DRAFT" && <button data-testid="po-start-button" className="primary-btn" onClick={onStart}><Hammer size={14} /> Mulai Produksi</button>}
              {order.status === "IN_PROGRESS" && <button data-testid="po-finish-button" className="primary-btn" onClick={onFinish}><CheckCircle2 size={14} /> Tandai Selesai</button>}
              {order.status === "IN_PROGRESS" && <button data-testid="po-cancel-production-button" className="danger-btn" onClick={onCancel}>Batalkan Produksi</button>}
              {order.status === "DRAFT" && <button data-testid="po-edit-button" className="secondary-btn" onClick={onEdit}>Edit</button>}
              {order.status === "DRAFT" && <button data-testid="po-delete-button" className="danger-btn" onClick={onDelete}><Trash2 size={14} /> Hapus</button>}
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
}
